import React from 'react';
import './BackgroundPanel.css';
import 'katex/dist/katex.min.css';
import { BlockMath, InlineMath } from 'react-katex';

interface BackgroundPanelProps {
  onClose: () => void;
}

export const BackgroundPanel: React.FC<BackgroundPanelProps> = ({ onClose }) => {
  return (
    <div className="background-panel">
      <div className="background-header">
        <h2>Background</h2>
        <button className="close-button" onClick={onClose}>×</button>
      </div>
      <div className="background-content">
        <h3>Introduction</h3>
        <p>
          Coastal ecosystems in northwest Spain face multiple stressors, including the impacts of mussel aquaculture and pressures from fishing. This area of the Galician coast is distinguished by the presence of estuarine inlets known as Rias. The Rias are sedimentary soft bed ecosystems characterised by mudflats, sandbanks and seagrass meadows.
        </p>
        <p>
          These ecosystems are under the influence of the northern part of the Canary Current upswelling, and are one of the highest primary production areas of the European coast. Consequently, Galicia hosts the largest mussel aquaculture in Europe, and is a world leader in shellfish production.
        </p>
        <p>
          The combined effects of mussel aquaculture and fishing pressure influences the communities associated with soft sedimentary bottoms of the Rias in multiple different ways. Increasing biodiversity improves the benthic community structure, enhances community resilience to adverse conditions, and provides a valuable buffer against potential climate related impacts.
        </p>
        <p>
          As part of the CLIMAREST project, a number of artificial reefs were deployed near floating rafts used for mussel cultivation. These artificial reefs are made of the same material as those previously deployed on the Harbour of Vigo in a move towards developing "green ports."
        </p>
        <p>
          In this Mussel Farm Digital Twin demonstrator, users can experiment with the biodiversity effects of selecting new areas for the placement of artificial reefs near mussel rafts. The demonstrator relies on biodiversity observations collected by divers through the course of the CLIMAREST project, and mathematical models to estimate biodiversity. This small scale demonstration provides the first step towards a tool for optimizing area selection for the deployment of biodiversity-enriching artificial reefs.
        </p>
        <h3>Biodiversity Models</h3>
        <p>
          Biodiversity (abundance and richness) is estimated from mathematical models. They are based on measurements of abundance and richness across different reef sizes (see <a href="https://www.jstor.org/stable/24320513">Borntrager and Farrell; 1992</a>) and (ii) different combinations of reefs and mussels farms (Daban et al; <i>in preparation</i>).
        </p>
        <h4>Single Reef</h4>
        <p>
          For a reef of volume <InlineMath math="V_\mathrm{Reef}" />, we estimate species abundance

          <BlockMath math="N(V_\mathrm{Reef}) = c_0 + c_1 V_\mathrm{Reef} + c_2 V_\mathrm{Reef}^2" />

          and richness

          <BlockMath math="S(V_\mathrm{Reef}) = b \left( 1 - e^{-a V_\mathrm{Reef}} \right) + c" />where the coefficients <InlineMath math="c_0 = 2.2" />, <InlineMath math="c_1 = 6.2 \times 10^{-4}" />, <InlineMath math="c_2 = 1.3 \times 10^{-7}" /> as well as <InlineMath math="a = 2.8 \times 10^{-4}" />, <InlineMath math="b = 5.2" />, <InlineMath math="b = 0.7" /> are derived from fitting to the data provided in XXX.
        </p>
        <h4>Multiple Reef</h4>
        <p>
          The total abundance <InlineMath math="N_\mathrm{Total}" /> is the sum of the abundances in each reef <InlineMath math="N_k" />, i.e. <BlockMath math="N_\mathrm{Total} = \sum_{k \leq M}^{M} N_k" />

          The combined richness <InlineMath math="S_\mathrm{Total}" /> is a weighted sum across all reefs, viz. <BlockMath math="S_\mathrm{Total} = \sum_{k \leq M}^{M} \frac{w_k}{w_0} S_k" /> where the weights <BlockMath math="\frac{w_k}{w_0} = \frac{e^{\, ln(a) + ln(b) - a k}}{e^{\, ln(a) + ln(b)}}" />

          are the (normalized) derivative of fitting <BlockMath math="S_k = b \left( 1 - e^{-a k} \right) + c" /> to the measurements of richness over <InlineMath math="k" /> reefs to be published in Daban et al (<i>in preparation</i>). Here, <InlineMath math="a = 0.3" /> and <InlineMath math="b = 20.0" />.
        </p>
      </div>
    </div>
  );
};
